import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { formatCurrency } from "../../lib/utils";
import axios from "axios";
import { Bell, Check, AlertTriangle, MessageCircle, X } from "lucide-react";

const API = `${window.location.origin}/api`;

export const NotificationsPopover = ({ whatsappNumber }) => {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const isArabic = language === "ar";

  useEffect(() => {
    if (open) {
      fetchNotifications();
      checkDebts();
    }
  }, [open]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const checkDebts = async () => {
    try {
      await axios.get(`${API}/notifications/check-debts`);
      fetchNotifications();
    } catch (error) {
      console.error("Error checking debts:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/mark-read/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications([]);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const sendWhatsAppReminder = (notification) => {
    if (!notification.customer_phone) return;
    
    let message = isArabic
      ? `*تذكير بدين مستحق*\n\n`
      : `*Debt Reminder*\n\n`;
    
    message += isArabic
      ? `مرحباً ${notification.customer_name || ""},\n\n`
      : `Hello ${notification.customer_name || ""},\n\n`;
    
    message += isArabic
      ? `نود تذكيركم بأن لديكم دين مستحق بقيمة *${notification.amount?.toLocaleString() || 0} د.ع*\n`
      : `We would like to remind you that you have an outstanding debt of *${notification.amount?.toLocaleString() || 0} د.ع*\n`;
    
    if (notification.due_date) {
      message += isArabic
        ? `تاريخ الاستحقاق: ${new Date(notification.due_date).toLocaleDateString("ar-IQ")}\n`
        : `Due Date: ${new Date(notification.due_date).toLocaleDateString("en-US")}\n`;
    }
    
    message += isArabic
      ? `\nنرجو التكرم بتسديد المبلغ في أقرب وقت ممكن.\n\nشكراً لتعاملكم معنا 🙏`
      : `\nPlease settle the amount at your earliest convenience.\n\nThank you for your business 🙏`;
    
    let cleanPhone = notification.customer_phone.replace(/\s+/g, "").replace(/^0/, "964");
    if (!cleanPhone.startsWith("+") && !cleanPhone.startsWith("964")) {
      cleanPhone = "964" + cleanPhone;
    }
    
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const unreadCount = notifications.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-slate-300 hover:text-white"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 bg-slate-800 border-slate-700 text-white p-0" 
        align="end"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <h3 className="font-semibold">
            {isArabic ? "الإشعارات" : "Notifications"}
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs text-slate-400 hover:text-white"
            >
              <Check className="w-3 h-3 me-1" />
              {isArabic ? "قراءة الكل" : "Mark all read"}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-72">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Bell className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">
                {isArabic ? "لا توجد إشعارات" : "No notifications"}
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="p-3 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-amber-500/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {notification.message}
                      </p>
                      {notification.amount && (
                        <p className="text-sm font-bold text-red-500 mt-1">
                          {formatCurrency(notification.amount)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-500 hover:text-white"
                      onClick={() => markAsRead(notification.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Actions */}
                  {notification.customer_phone && (
                    <div className="mt-2 pt-2 border-t border-slate-700">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendWhatsAppReminder(notification)}
                        className="w-full border-green-600 text-green-500 hover:bg-green-600 hover:text-white text-xs"
                      >
                        <MessageCircle className="w-3 h-3 me-1" />
                        {isArabic ? "إرسال تذكير واتساب" : "Send WhatsApp Reminder"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
